// CheckMaty EntityTypeConfigs for the @plasser/ui EntityPicker (TES-1878).
// ---------------------------------------------------------------------------
// These configs are CheckMaty-specific and intentionally separated from the
// generic shells in @plasser/ui (extraction discipline, TES-1600). They declare
// the *conceptual* searchable attributes + table columns + QuickCreate forms.
//
// i18n (TES-2035): the display strings (labelSingular/Plural, FilterPill labels,
// column labels, QuickCreate labels/placeholders) are resolved via t()-factories
// so en/ja users no longer see hard-coded German. The DATA keys (type,
// searchableAttributes, column/filter `key`, createPermission) are stable and
// never localized. Consumers memoize `getXPickerConfig(t)` on the stable t.
//
// R-USTORY-04 (real schema is law): the LH/epic names some attributes that do
// not yet exist as columns (job_title, department, manager, standort, owner,
// group_type). The picker searches over a generic `attributes` map, so the
// SearchProvider maps whatever real columns/RPC fields are available into those
// keys. Once A1a (search RPC + has_permission_scoped) merges, the provider
// returns the full attribute set; until then it maps the columns that exist
// today (types.ts) and leaves the rest empty. The config shape never changes.

import type { EntityTypeConfig } from "@plasser/ui";
import type { TFunction } from "i18next";

/** Groups (real table: team_group ⊂ teams). "Mechanical Indien" → standort=Indien. */
export function getGroupPickerConfig(t: TFunction): EntityTypeConfig {
  return {
    type: "group",
    labelSingular: t("pickers.group.singular"),
    labelPlural: t("pickers.group.plural"),
    searchableAttributes: ["standort", "group_type", "owner", "department", "team"],
    filterPills: [
      { key: "standort", label: t("pickers.group.filterStandort") },
      { key: "group_type", label: t("pickers.group.filterType") },
    ],
    columns: [
      { key: "name", label: t("pickers.group.colName") },
      { key: "team", label: t("pickers.group.colTeam") },
      { key: "standort", label: t("pickers.group.colStandort") },
    ],
    quickCreate: {
      label: t("pickers.group.quickCreateLabel"),
      createPermission: "teams:manage",
      fields: [
        {
          key: "name",
          label: t("pickers.group.fieldName"),
          required: true,
          placeholder: t("pickers.group.fieldNamePlaceholder"),
        },
        { key: "description", label: t("pickers.group.fieldDescription"), type: "text" },
      ],
    },
    defaultView: "list",
    multiSelect: false,
  };
}

/** People (real table: profiles). "Schmidt Indien" → last_name=Schmidt + standort=Indien. */
export function getPeoplePickerConfig(t: TFunction): EntityTypeConfig {
  return {
    type: "person",
    labelSingular: t("pickers.person.singular"),
    labelPlural: t("pickers.person.plural"),
    searchableAttributes: ["email", "job_title", "department", "manager", "standort"],
    filterPills: [
      { key: "department", label: t("pickers.person.filterDepartment") },
      { key: "standort", label: t("pickers.person.filterStandort") },
    ],
    columns: [
      { key: "name", label: t("pickers.person.colName") },
      { key: "email", label: t("pickers.person.colEmail") },
      { key: "department", label: t("pickers.person.colDepartment") },
    ],
    // People are provisioned via Entra/admin, never QuickCreated from a picker.
    defaultView: "list",
    multiSelect: true,
  };
}

/** Machines (real table: machines). */
export function getMachinePickerConfig(t: TFunction): EntityTypeConfig {
  return {
    type: "machine",
    labelSingular: t("pickers.machine.singular"),
    labelPlural: t("pickers.machine.plural"),
    searchableAttributes: [
      "machine_number",
      "serial_number",
      "manufacturer",
      "model",
      "location",
      "commission_number",
    ],
    filterPills: [
      { key: "manufacturer", label: t("pickers.machine.filterManufacturer") },
      { key: "location", label: t("pickers.machine.filterLocation") },
    ],
    columns: [
      { key: "name", label: t("pickers.machine.colName") },
      { key: "machine_number", label: t("pickers.machine.colMachineNumber") },
      { key: "manufacturer", label: t("pickers.machine.colManufacturer") },
    ],
    defaultView: "table",
    multiSelect: false,
  };
}

/** Milestones (real table: machine_milestone_catalog). */
export function getMilestonePickerConfig(t: TFunction): EntityTypeConfig {
  return {
    type: "milestone",
    labelSingular: t("pickers.milestone.singular"),
    labelPlural: t("pickers.milestone.plural"),
    searchableAttributes: ["code", "bezeichnung"],
    filterPills: [{ key: "relevant_for_inspection", label: t("pickers.milestone.filterRelevant") }],
    columns: [
      { key: "name", label: t("pickers.milestone.colName") },
      { key: "code", label: t("pickers.milestone.colCode") },
    ],
    defaultView: "list",
    multiSelect: true,
  };
}

/** Checklist templates (real table: checklists). */
export function getChecklistTemplatePickerConfig(t: TFunction): EntityTypeConfig {
  return {
    type: "checklist_template",
    labelSingular: t("pickers.checklistTemplate.singular"),
    labelPlural: t("pickers.checklistTemplate.plural"),
    searchableAttributes: ["version", "status", "description"],
    filterPills: [{ key: "status", label: t("pickers.checklistTemplate.filterStatus") }],
    columns: [
      { key: "name", label: t("pickers.checklistTemplate.colName") },
      { key: "version", label: t("pickers.checklistTemplate.colVersion") },
      { key: "status", label: t("pickers.checklistTemplate.colStatus") },
    ],
    defaultView: "list",
    multiSelect: false,
  };
}

/** All picker configs, resolved for the current language. */
export function getPickerConfigs(t: TFunction) {
  return {
    group: getGroupPickerConfig(t),
    person: getPeoplePickerConfig(t),
    machine: getMachinePickerConfig(t),
    milestone: getMilestonePickerConfig(t),
    checklist_template: getChecklistTemplatePickerConfig(t),
  } as const;
}

export type PickerConfigKey = keyof ReturnType<typeof getPickerConfigs>;
